import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  applyMigration,
  createMigration,
  deleteMigration,
  fetchMigrationStats,
  listMigrations,
  MIGRATION_PRINCIPLES,
  migrationStatusMeta,
  nextVersion,
  rollbackMigration,
  type CoreMigration,
} from '@/lib/coreMigrations';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function MigrationsTab({ org }: Props) {
  const [migrations, setMigrations] = useState<CoreMigration[]>([]);
  const [stats, setStats] = useState({ total: 0, applied: 0, pending: 0, rollbacks: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', description: '', rollbackSql: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, s] = await Promise.all([listMigrations(), fetchMigrationStats()]);
      setMigrations(list);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCreate = async () => {
    if (!form.name.trim()) { setError('Le nom de la migration est requis.'); return; }
    setSaving(true);
    setError('');
    try {
      await createMigration({
        version: nextVersion(migrations),
        name: form.name.trim(),
        description: form.description || null,
        rollbackSql: form.rollbackSql || null,
      });
      setForm({ name: '', description: '', rollbackSql: '' });
      setNotice('Migration créée (en attente d’application).');
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Création impossible.'); } finally { setSaving(false); }
  };

  const onApply = async (m: CoreMigration) => {
    setBusy(m.id);
    try { await applyMigration(m.id, 'dashboard'); setNotice(`Migration ${m.version} appliquée.`); await load(); } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(null); }
  };

  const onRollback = async (m: CoreMigration) => {
    setBusy(m.id);
    setError('');
    try { await rollbackMigration(m.id); setNotice(`Migration ${m.version} annulée.`); await load(); } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(null); }
  };

  const onDelete = async (m: CoreMigration) => {
    setBusy(m.id);
    try { await deleteMigration(m.id); await load(); } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Migrations</span> : toutes les modifications de schéma
        sont versionnées. On ne modifie jamais la base en production sans migration contrôlée — avec rollback
        lorsque c’est possible, version et documentation.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
      {notice && <div className="p-3 bg-secondary-50 border border-secondary-200 rounded-md text-xs text-secondary-800 flex items-center justify-between"><span>{notice}</span><button onClick={() => setNotice('')} className="cursor-pointer"><i className="ri-close-line"></i></button></div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-git-commit-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Migrations</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.applied}</p>
          <p className="text-xs text-foreground-500">Appliquées</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-time-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.pending}</p>
          <p className="text-xs text-foreground-500">En attente</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-arrow-go-back-line text-lg text-foreground-500"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.rollbacks}</p>
          <p className="text-xs text-foreground-500">Rollbacks définis</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-shield-check-line text-primary-600"></i>Règles de migration</h4>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {MIGRATION_PRINCIPLES.map((p, i) => (
            <li key={i} className="text-[11px] text-foreground-600 flex items-start gap-1.5"><i className="ri-checkbox-blank-circle-line text-[7px] mt-1.5 text-primary-500"></i>{p}</li>
          ))}
        </ul>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-git-branch-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Historique versionné</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : migrations.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune migration.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {migrations.map((m) => {
              const sm = migrationStatusMeta(m.status);
              return (
                <div key={m.id} className="px-4 py-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0"><i className="ri-git-commit-line"></i></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-800 truncate"><code className="bg-background-100 px-1 rounded text-xs">{m.version}</code> {m.name}</p>
                      <p className="text-[11px] text-foreground-400 truncate">{m.description}{m.applied_at ? ` · ${new Date(m.applied_at).toLocaleString('fr-FR')}` : ''}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${sm.className}`}>{sm.label}</span>
                  </div>
                  <div className="mt-2 pl-12 flex flex-wrap gap-1.5">
                    {m.status !== 'applied' && (
                      <button onClick={() => onApply(m)} disabled={busy === m.id} className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40">Appliquer</button>
                    )}
                    {m.status === 'applied' && (
                      <button onClick={() => onRollback(m)} disabled={busy === m.id || !m.rollback_sql} title={!m.rollback_sql ? 'Aucun rollback défini' : ''} className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40">Rollback</button>
                    )}
                    {m.status !== 'applied' && (
                      <button onClick={() => onDelete(m)} disabled={busy === m.id} className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40">Supprimer</button>
                    )}
                    {busy === m.id && <i className="ri-loader-4-line animate-spin text-foreground-400"></i>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-add-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Nouvelle migration</h4>
          <span className="ml-auto text-[11px] text-foreground-400">Version auto : <code className="bg-background-100 px-1 rounded">{nextVersion(migrations)}</code></span>
        </div>
        <div className="p-4 space-y-3">
          <input className={INPUT} placeholder="Nom (ex : add_client_loyalty_column)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className={INPUT} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={300} />
          <textarea className={`${INPUT} h-20 font-mono text-xs`} placeholder="Rollback SQL (optionnel)" value={form.rollbackSql} onChange={(e) => setForm({ ...form, rollbackSql: e.target.value })}></textarea>
          <button onClick={onCreate} disabled={saving} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Créer la migration
          </button>
        </div>
      </div>
    </div>
  );
}